import { noop } from 'lodash-es';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useAutoPricingPreview } from 'src/components/Listings/MapWithCompListingsPreview/MapWithCompListingsPreview.hooks';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { globalMobileWrapper } from 'src/core/themes/themeContract.css';
import { Stack } from 'src/core/ui';
import { useListingInitializeFromDeeplink } from 'src/hooks/useListingInitializeFromDeepLink';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import { InventoryDeeplinkQueryParam } from 'src/utils/constants/constants';
import { addDeepLinkUrlPart } from 'src/utils/deepLinkUtils';
import {
  Event,
  Listing,
  ListingDetails,
  ListingDetailsPricingUpdates,
  ListingGroup,
  ListingGroupAutoPriceSettings,
  ListingPriceCalculation,
  SectionRowScoreRecommendations,
  SelectedSectionSettings,
} from 'src/WebApiController';

import { EventMapContextProvider } from '../EventMapContext';
import { MobileListingDrawer } from './MobileListingDrawer';
import * as styles from './MobileListingDrawerContext.css';
import { useMobileListingDrawerPricingSettings } from './MobileListingDrawerContext.hooks';
import { MobileListingGroupDrawer } from './MobileListingGroupDrawer';
import { MobileListingPricingInputDrawer } from './MobileListingPriceInputDrawer';

export enum ViewMode {
  Details,
  SideView,
  Exit,
}

/**
 * MobileListingDrawerContext to include EventMapContextProvider
 */
export const MobileListingDrawerContextWrapper = ({
  children,
}: PropsWithChildren) => {
  const isMobileLayout = useMatchMedia('mobile');
  const {
    eventsTransformed,
    eventsExpansion: { toggleListItemExpansion },
  } = useCatalogDataContext();
  const { setActivePosEntity } = useActivePosEntityContext<ListingDetails>();
  const { deeplinkListing, onCleanUpDeeplinkListingkey } =
    useListingInitializeFromDeeplink();

  const [selectedListing, setSelectedListing] = useState<Listing | null>();
  const [selectedListingGroup, setSelectedListingGroup] =
    useState<ListingGroup | null>();
  const event = useMemo(
    () =>
      eventsTransformed?.find(
        (e) =>
          e.event.virtId === selectedListing?.viagVirtId ||
          e.event.virtId === selectedListingGroup?.viagVirtId
      ),
    [
      eventsTransformed,
      selectedListing?.viagVirtId,
      selectedListingGroup?.viagVirtId,
    ]
  );

  // To suppor the deeplink
  useEffect(() => {
    if (deeplinkListing) {
      setSelectedListing(deeplinkListing);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(deeplinkListing)]);

  // !important this is to support deeplink in the portfolio page
  // where event may not be expanded
  useEffect(() => {
    if (event?.event?.virtId && !event?.entities?.listings?.length) {
      toggleListItemExpansion([event.event.virtId]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.event?.virtId, event?.entities?.listings?.length]);

  const eventListings = useMemo(() => {
    return ((event?.entities ?? {}).listings ?? []).flatMap(
      flattenListingGroup
    ) as Listing[];
  }, [event?.entities]);

  // !importatnt, this is to refresh the selected listing when there is any updates in catalog
  useEffect(() => {
    const updatedListing = eventListings.find(
      (l) => l.id === selectedListing?.id
    );
    if (!updatedListing) {
      return;
    }

    if (!selectedListing || updatedListing?.rowVer >= selectedListing?.rowVer) {
      setSelectedListing(updatedListing);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventListings]);

  const onSelectListing = useCallback(
    (listing: Listing) => {
      if (listing.isLtGrp) {
        return;
      }
      // Only allow click when not a listing group
      setActivePosEntity(listing.id, listing.idOnMkp, true);
      addDeepLinkUrlPart(InventoryDeeplinkQueryParam, String(listing.id));
      setSelectedListing(listing);
    },
    [setActivePosEntity]
  );

  const onCloseListing = useCallback(() => {
    setSelectedListing(undefined);
    onCleanUpDeeplinkListingkey();
  }, [onCleanUpDeeplinkListingkey]);

  if (!isMobileLayout) {
    return <>{children}</>;
  }

  return (
    <EventMapContextProvider event={event?.event} venueMapOnly>
      <MobileListingDrawerContextProvider
        event={event?.event}
        eventListings={eventListings}
        selectedListing={selectedListing ?? undefined}
        selectedListingGroup={selectedListingGroup ?? undefined}
        onSelectListing={onSelectListing}
        onSelectListingGroup={setSelectedListingGroup}
        onCloseListing={onCloseListing}
      >
        <Stack
          direction="column"
          className={globalMobileWrapper}
          style={{ height: '100%', width: '100%' }}
        >
          {children}
        </Stack>
      </MobileListingDrawerContextProvider>
    </EventMapContextProvider>
  );
};

export type IMobileListingDrawerContext = {
  viewMode: ViewMode;
  pricingSettings?: ListingDetailsPricingUpdates;
  listingGroupPricingSettings?: ListingGroupAutoPriceSettings;
  listingPriceCalculation?: ListingPriceCalculation;
  eventListings?: Listing[];
  selectedListing?: Listing;
  selectedListingGroup?: ListingGroup;
  sectionRowRecommendations?: SectionRowScoreRecommendations | undefined;
  editingListing?: Listing;
  onNavViewMode: (params: { back?: boolean; viewMode?: ViewMode }) => void;
  toggleEditingListing: (value?: Listing) => void;
  onSelectListing: (listing: Listing) => void;
  onSelectListingGroup: (listingGroup: ListingGroup | null) => void;
  onSaveListingPrice: (
    listPrice: number,
    listing?: Listing,
    callback?: () => void
  ) => void;
  onSavePricingSettings: (
    selectedSectionSettings: SelectedSectionSettings | null
  ) => void;
  onSaveListingGroupPricingSettings: (
    selectedSectionSettings: SelectedSectionSettings | null
  ) => void;
};

const emptyContext: IMobileListingDrawerContext = {
  viewMode: ViewMode.Details,
  onNavViewMode: noop,
  toggleEditingListing: noop,
  onSelectListing: noop,
  onSelectListingGroup: noop,
  onSaveListingPrice: noop,
  onSavePricingSettings: noop,
  onSaveListingGroupPricingSettings: noop,
};

export const MobileListingDrawerContext =
  createContext<IMobileListingDrawerContext>(emptyContext);

export const useMobileListingDrawerContext = () =>
  useContext(MobileListingDrawerContext);

export const MobileListingDrawerContextProvider = ({
  event,
  eventListings,
  selectedListing,
  selectedListingGroup,
  onSelectListing,
  onSelectListingGroup,
  onCloseListing,
  children,
}: PropsWithChildren & {
  event?: Event;
  eventListings?: Listing[];
  selectedListing?: Listing;
  selectedListingGroup?: ListingGroup;
  onSelectListing: (listing: Listing) => void;
  onSelectListingGroup: (listingGroup: ListingGroup | null) => void;
  onCloseListing: () => void;
}) => {
  const [editingListing, toggleEditingListing] = useState<Listing | undefined>(
    undefined
  );
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.Details);
  const {
    isLoading,
    pricingSettingsInput,
    listingGroupPricingSettings,
    sectionRowRecommendations,
    onSaveListingPrice,
    onSavePricingSettings,
    onSaveListingGroupPricingSettings,
  } = useMobileListingDrawerPricingSettings(
    selectedListing ?? editingListing ?? undefined,
    selectedListingGroup,
    event
  );

  const { priceCalcQuery } = useAutoPricingPreview(
    event?.viagId ?? null,
    selectedListing?.id,
    pricingSettingsInput,
    selectedListing?.officeId
  );

  const onNavViewMode = useCallback(
    ({
      viewMode: mode,
      back = true,
    }: {
      back?: boolean;
      viewMode?: ViewMode;
    }) => {
      if (mode) {
        setViewMode(mode);
        return;
      }
      if (back) {
        if (viewMode === ViewMode.SideView) {
          setViewMode(ViewMode.Details);
        } else {
          onSelectListingGroup(null);
          onCloseListing();
        }
      } else {
        if (viewMode === ViewMode.Details) {
          setViewMode(ViewMode.SideView);
        }
      }
    },
    [onCloseListing, onSelectListingGroup, viewMode]
  );

  return (
    <MobileListingDrawerContext.Provider
      value={{
        viewMode,
        eventListings,
        selectedListing: selectedListing ?? undefined,
        selectedListingGroup: selectedListingGroup ?? undefined,
        pricingSettings: pricingSettingsInput,
        listingGroupPricingSettings: listingGroupPricingSettings,
        sectionRowRecommendations,
        listingPriceCalculation: priceCalcQuery.data ?? undefined,
        editingListing,
        onNavViewMode,
        onSelectListing,
        onSelectListingGroup,
        toggleEditingListing,
        onSaveListingPrice,
        onSavePricingSettings,
        onSaveListingGroupPricingSettings,
      }}
    >
      {children}
      <MobileListingGroupDrawer event={event} />
      <MobileListingDrawer event={event} />
      <MobileListingPricingInputDrawer />
      <LoadingOverlay isLoading={isLoading} />
    </MobileListingDrawerContext.Provider>
  );
};

const LoadingOverlay = ({ isLoading }: { isLoading: boolean }) => {
  if (!isLoading) {
    return null;
  }

  return createPortal(
    <div className={styles.loadingOverlay}>
      <PosSpinner />
    </div>,
    document.body
  );
};
